import { MARKS } from '@contentful/rich-text-types';
import type { DocumentWithEmbeddedEntries } from 'libs/contentful/types';
import { renderSuperscript } from 'utils/superscriptUtils';
import RichTextInline from 'components/shared/RichTextInline/RichTextInline';

interface DisplayHeadlineProps {
  headline: DocumentWithEmbeddedEntries;
  level: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  className: string;
  lineClassName?: string;
  accentClassName?: string;
}

const hasContent = (paragraph: DocumentWithEmbeddedEntries['json']['content'][number]) =>
  'content' in paragraph && paragraph.content.some(node => node.nodeType !== 'text' || node.value.trim() !== '');

// Each paragraph is one animated line, and the last of several is the accent. Lines come from the authored paragraphs
// rather than measured text so the headline renders, and starts animating, on the server.
export function DisplayHeadline({
  headline,
  level: Heading,
  className,
  lineClassName = '',
  accentClassName,
}: DisplayHeadlineProps) {
  const paragraphs = headline.json.content.filter(hasContent);
  return (
    <Heading className={className}>
      {paragraphs.map((paragraph, index) => {
        const accent = paragraphs.length > 1 && index === paragraphs.length - 1;
        return (
          <span key={index} className="block overflow-hidden">
            <span
              className={`block ${accent ? `text-fern ${accentClassName ?? lineClassName}` : lineClassName}`}
              style={{ transitionDelay: `${index * 100}ms` }}>
              <RichTextInline
                inline={{ ...headline, json: { ...headline.json, content: [paragraph] } }}
                additionalOptions={{
                  renderMark: {
                    [MARKS.BOLD]: children => <strong className="font-light text-fern">{children}</strong>,
                    [MARKS.SUPERSCRIPT]: renderSuperscript,
                  },
                }}
              />{' '}
            </span>
          </span>
        );
      })}
    </Heading>
  );
}
